import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation, Inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MenuItem, SelectItem, Footer } from 'primeng/api';

import { SensorFailure, FailureAuditTypeAhead } from '../../../common/model';
import { AuthService, GrowlService, ToolTipService, WorkflowService } from '../../../common/service';
import { Permission } from '../../../common/enum';
import { catchError, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'sensor-fail-edit',
  templateUrl: './sensor-fail-edit.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, FormsModule, ReactiveFormsModule, InputTextModule, AutoCompleteModule, SplitButtonModule, ButtonDirective, TooltipModule, DialogModule, ConfirmDialogModule, CheckboxModule]
})
export class SensorFailureEditComponent implements OnInit {
  @Input() visible: boolean;
  @Input() sensorFailure: SensorFailure;
  @Input() podList: SelectItem[];
  @Input() podFilter: SelectItem[];
  @Input() submitted: boolean;
  @Input() showConfirm: boolean;
  @Input() showContact = false;
  @Input() showAction: boolean;
  @Input() showClose: boolean;
  @Input() showSplitButtonHelp: boolean;

  @Input() showFalsePos: boolean;

  // @Input() showNotNeeded: boolean;
  // @Input() showNotRequired: boolean;
  // @Input() showNoAction: boolean;
  // @Input() showConfirmedAction: boolean;
  // @Input() showShipping: boolean;
  // @Input() showSensorReturned: boolean;
  // @Input() showOnhold: boolean;
  @Output() saveEvent: EventEmitter<any> = new EventEmitter();
  @Output() cancelEvent: EventEmitter<any> = new EventEmitter();
  Permission = Permission;
  editForm: FormGroup;

  typeAheadSuggestions: FailureAuditTypeAhead[] = [];
  typeAheadFiltered: FailureAuditTypeAhead[] = [];
  noteSelected: any;
  failure_date_formatted: string;
  installation_date_formatted: string;

  contactOption = 'Ship Sensor';
  actionOption = 'Sensor Changed';
  closeOption = 'Sensor Replaced';
  showChangedOnly = false;
  showReturnedOnly = false;
  showEmailOnly = false;
  confirmItems: MenuItem[];
  contactItems: MenuItem[];
  actionItems: MenuItem[];
  completionItems: MenuItem[];
  tt: string;
  constructor(
    @Inject(AuthService) public authService: AuthService,
    private confirmationService: ConfirmationService,
    private workflowService: WorkflowService,
    @Inject(GrowlService) public growlService: GrowlService) { }

  ngOnInit() {
    this.submitted = false;
    this.sensorFailure = new SensorFailure();

    this.editForm = new FormGroup({
      pod_serial_number: new FormControl(this.sensorFailure.pod_serial_number),
      distributor_name: new FormControl(this.sensorFailure.distributor_name),
      owner_name: new FormControl(this.sensorFailure.owner_name),
      location_name: new FormControl(this.sensorFailure.location_name),
      sale_type: new FormControl(this.sensorFailure.sale_type),
      sensor_serial_number: new FormControl(this.sensorFailure.sensor_serial_number),
      sensor_type: new FormControl(this.sensorFailure.sensor_type),
      fail_reason: new FormControl(this.sensorFailure.fail_reason),
      installation_date: new FormControl(this.sensorFailure.installation_date_fmt),
      alert_status_description: new FormControl(this.sensorFailure.alert_status_description),
      pod_status: new FormControl(this.sensorFailure.pod_status),
      failure_date: new FormControl(this.sensorFailure.failure_date_fmt),
      shipped_date: new FormControl(this.sensorFailure.shipped_date_fmt),
      age_in_months: new FormControl(this.sensorFailure.age_in_months),
      replacement_sensor_serial: new FormControl(this.sensorFailure.replacement_sensor_serial),
      ship_sensors_to_owner: new FormControl(this.sensorFailure.ship_sensors_to_owner),
      block_sending_sensors: new FormControl(this.sensorFailure.block_sending_sensors),
      notes: new FormControl(this.sensorFailure.notes)
    });
  }

  addNote() {
    this.submitted = true;
    if (this.noteSelected === undefined) {
      this.submitted = false;
      this.growlService.error(`No note entered.`);
      return;
    } else {
      // if (isString(this.noteSelected)) {  }
      if (typeof this.noteSelected === 'string') {
        this.sensorFailure.notes = this.noteSelected;
      } else {
        this.sensorFailure.notes = this.noteSelected.notes;
      }
    }
    if (this.editForm.valid) {
      this.workflowService.postNote(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success(`New note added.`);
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(error);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  autoComplete(event) {
    this.typeAheadFiltered = [];
    for (let i = 0; i < this.typeAheadSuggestions.length; i++) {
      const typeAhead = this.typeAheadSuggestions[i];
      if (typeAhead.notes.toLowerCase().startsWith(event.query.toLowerCase()) === true) {
        this.typeAheadFiltered.push(typeAhead);
      }
    }
  }

  closeFailure() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postCloseFailure(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Sensor Failure Closed');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  confirm() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        //        if (isString(this.noteSelected)) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.sensorFailure.shipped = (this.sensorFailure.replacement_sensor_serial !== null);
      this.sensorFailure.not_needed = false;
      this.sensorFailure.not_required = false;
      this.workflowService.postAllocateReplacementSensor(this.sensorFailure).pipe(tap((r) => {
        if (this.sensorFailure.shipped) {
          this.growlService.info(`Replacement sensor  ${this.sensorFailure.replacement_sensor_serial} shipped`);
        } else {
          this.growlService.info(`Failure confirmed`);
        }
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  emailNote() {
    this.submitted = true;
    if (this.noteSelected === undefined) {
      this.submitted = false;
      this.growlService.error(`No note entered.`);
      return;
    } else {
      // if (isString(this.noteSelected)) {
      if (typeof this.noteSelected === 'string') {
        this.sensorFailure.notes = this.noteSelected;
      } else {
        this.sensorFailure.notes = this.noteSelected.notes;
      }
    }
    if (this.editForm.valid) {
      this.workflowService.emailNote(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success(`Email note added.`);
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(error);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  exit() {
    this.cancelEvent.emit(null);
    this.sensorFailure = new SensorFailure();
    this.submitted = false;
  }

  falsePos() {
    this.submitted = true;
    if ([20, 25].indexOf(this.sensorFailure.alert_status) >= 0) {
      this.actionFalsePositive();
    } else {
      this.confirmationService.confirm({
        message: 'Please confirm that the sensor has not failed',
        header: 'Confirmation',
        icon: 'pi pi-thumbs-up',
        key: 'areYouSure',
        accept: () => {
          this.actionFalsePositive();
        },
        reject: () => {
          this.submitted = false;
        },
      });
    }
  }

  actionFalsePositive() {
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        //        if (isString(this.noteSelected)) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postSetFalsePositive(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('False positive confirmed');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  loadLists() {
    this.workflowService.getSensorNotes().pipe(tap((data) => {
      this.typeAheadSuggestions = data;
    }), catchError((error) => {
      this.growlService.error(error);
      return error;
    })).subscribe();
  }

  noAction() {
    this.submitted = true;
    if (this.sensorFailure.replacement_sensor_number === null) {
      if (this.editForm.valid) {
        if (this.noteSelected !== undefined) {

          //          if (isString(this.noteSelected)) {
          if (typeof this.noteSelected === 'string') {
            this.sensorFailure.notes = this.noteSelected;
          } else {
            this.sensorFailure.notes = this.noteSelected.notes;
          }
        }
        this.sensorFailure.shipped = false;
        this.sensorFailure.not_needed = false;
        this.sensorFailure.not_required = false;
        this.sensorFailure.no_action = true;
        this.workflowService.postCloseFailure(this.sensorFailure).pipe(tap((r) => {
          this.growlService.success('Not action to be taken, confirmed!');
          this.submitted = false;
          this.saveEvent.emit(null);
        }), catchError((error) => {
          this.submitted = false;
          this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
          return error;
        })).subscribe();
      }
    } else {
      this.submitted = false;
      this.growlService.error(`Replacement sensor ${this.sensorFailure.replacement_sensor_number} allocated but "no action" specified!`);
    }
  }

  notNeeded() {
    this.submitted = true;
    /*
     if (this.sensorFailure.replacement_sensor_number === null) {
    */
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        //          if (isString(this.noteSelected)) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.sensorFailure.shipped = false;
      this.sensorFailure.not_needed = true;
      this.sensorFailure.not_required = false;
      this.sensorFailure.no_action = false;
      this.workflowService.postAllocateReplacementSensor(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Not needed by customer confirmed!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    }
    /*    } else {
          this.submitted = false;
          this.growlService.error(`Replacement sensor ${this.sensorFailure.replacement_sensor_number} allocated but not needed!`);
        }
    */
  }

  notRequired() {
    this.submitted = true;
    if (this.sensorFailure.replacement_sensor_number === null) {
      if (this.editForm.valid) {
        if (this.noteSelected !== undefined) {
          //          if (isString(this.noteSelected)) {
          if (typeof this.noteSelected === 'string') {
            this.sensorFailure.notes = this.noteSelected;
          } else {
            this.sensorFailure.notes = this.noteSelected.notes;
          }
        }
        this.sensorFailure.shipped = false;
        this.sensorFailure.not_needed = false;
        this.sensorFailure.not_required = true;
        this.sensorFailure.no_action = false;
        this.workflowService.postAllocateReplacementSensor(this.sensorFailure).pipe(tap((r) => {
          this.growlService.success('Not required confirmed!');
          this.submitted = false;
          this.saveEvent.emit(null);
        }), catchError((error) => {
          this.submitted = false;
          this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
          return error;
        })).subscribe();
      }
    } else {
      this.submitted = false;
      this.growlService.error(`Replacement sensor ${this.sensorFailure.replacement_sensor_number} allocated but not required!`);
    }
  }

  onHoldInvestigation() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        //       if (isString(this.noteSelected)) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postOnHoldInvestigation(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('On hold investigation confirmed!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  onHoldTesting() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        //        if (isString(this.noteSelected)) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postOnHoldTesting(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('On hold testing confirmed!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct`);
    }
  }

  quoteAccepted() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postQuoteAccepted(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Quote Sent Confirmed');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  sendQuote() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postSendQuote(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Quote sent confirmed');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  sensorChanged() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postSensorChanged(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Sensor changed!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(error);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  sensorNotReturned() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postCloseFailure(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Sensor Not Returned!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(error);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  sensorReturned() {
    this.submitted = true;
    if (this.editForm.valid) {
      if (this.noteSelected !== undefined) {
        if (typeof this.noteSelected === 'string') {
          this.sensorFailure.notes = this.noteSelected;
        } else {
          this.sensorFailure.notes = this.noteSelected.notes;
        }
      }
      this.workflowService.postSensorReturned(this.sensorFailure).pipe(tap((r) => {
        this.growlService.success('Sensor Returned!');
        this.submitted = false;
        this.saveEvent.emit(null);
      }), catchError((error) => {
        this.submitted = false;
        this.growlService.error(error);
        return error;
      })).subscribe();
    } else {
      this.submitted = false;
      this.growlService.error(`Please correct.`);
    }
  }

  shipping() {
    this.submitted = true;
    if (this.sensorFailure.replacement_sensor_serial !== null) {
      if (this.editForm.valid) {
        if (this.noteSelected !== undefined) {
          //          if (isString(this.noteSelected)) {
          if (typeof this.noteSelected === 'string') {
            this.sensorFailure.notes = this.noteSelected;
          } else {
            this.sensorFailure.notes = this.noteSelected.notes;
          }
        }
        if (this.sensorFailure.replacement_sensor_serial.length === 10 && this.sensorFailure.replacement_sensor_serial.substr(0, 1) === '0') {
          this.sensorFailure.replacement_sensor_serial = this.sensorFailure.replacement_sensor_serial.substr(1, 9);
        }
        this.growlService.info(`Replacement sensor  ${this.sensorFailure.replacement_sensor_serial}`);
        this.sensorFailure.shipped = true;
        this.sensorFailure.not_needed = false;
        this.sensorFailure.not_required = false;
        this.sensorFailure.no_action = false;
        this.workflowService.postAllocateReplacementSensor(this.sensorFailure).pipe(tap((r) => {
          this.growlService.success('Sensor shipped');
          this.submitted = false;
          this.saveEvent.emit(null);
        }), catchError((error) => {
          this.submitted = false;
          this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
          return error;
        })).subscribe();
      } else {
        this.submitted = false;
        this.growlService.error(`Please correct`);
      }
    } else {
      this.submitted = false;
      this.growlService.error(`Please add a replacement sensor serial number.`);
    }
  }

  contactDefault() {
    switch (this.sensorFailure.alert_status) {
      case 4:  //       4	    | Confirmed out of warranty
        this.sendQuote();
        break;
      case 6: //       6	    | Awaiting customer PO
        this.quoteAccepted();
        break;
      case 5: //       5	    | Confirmed
      case 7: //       7	    | Quote accepted
      case 8: //       8	    | Confirmed - customer use own stock
        this.shipping();
        break;
      default:
        this.emailNote();
    }
  }

  actionDefault() {
    switch (this.sensorFailure.alert_status) {
      case 45:
        this.sensorReturned();
        break;
      case 8:   //       8	    | Confirmed - customer use own stock
      case 10:  //      10	    | Sensor Allocated
      case 15:  //      15	    | Sensor Shipped
      case 30:  //      30	    | Old sensor returned - Need replacement confirmed
      case 35:  //      35	    | Confirmed - EI to replace
      default:
        this.sensorChanged();
    }
  }

  showButtons() {
    this.confirmItems = [];
    this.contactItems = [];
    this.actionItems = [];
    this.completionItems = [];
    const contacts = this.sensorFailure.sensor_failure_audits.sort((a, b) => a.sensor_failure_audit_number - b.sensor_failure_audit_number);
    const lastContact = contacts.slice(this.sensorFailure.sensor_failure_audits.length - 1, this.sensorFailure.sensor_failure_audits.length);
    /* +=========+ */
    /* | Confirm | */
    /* +=========+ */
    /*                Reported, On hold - Testing, Investigating */
    this.showConfirm = ([0, 20, 25].indexOf(this.sensorFailure.alert_status) >= 0);
    /*
        Failed sensor – Out of warranty
        Failed sensor – In warranty
        Failed sensor – Awaiting PO
        Quote accepted
        Customer to use own stock
        Customer to use allocated stock
        Replacement sensor shipped
        Old sensor returned - Need replacement confirmed
        EI to replace
    */
    this.showFalsePos = ([4, 5, 6, 7, 8, 10, 15, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0);

    if (this.has(Permission.Sensors_Failure_Full_Control)) {
      if ([0].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.confirmItems.push(
          {
            label: 'Investigate', icon: 'pi pi-search', command: () => {
              this.onHoldInvestigation();
            }
          });
      }

      if ([0, 20, 25].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.confirmItems.push(
          {
            label: 'False Positive', icon: 'pi pi-thumbs-up', command: () => {
              this.falsePos();
            }
          });
      }

      /* +=========+ */
      /* | Contact | */
      /* +=========+ */

      // Alert_Status	| Status_Description
      //       4	    | Confirmed out of warranty
      //       5	    | Confirmed
      //       6	    | Awaiting customer PO
      //       7	    | Quote accepted
      //       8	    | Confirmed - customer use own stock
      //      10	    | Sensor Allocated
      //      15	    | Sensor Shipped
      //      20	    | On hold - Testing
      //      25	    | On hold - Investigating
      //      30	    | Old sensor returned - Need replacement confirmed
      //      35	    | Confirmed - EI to replace

      if (lastContact !== undefined && lastContact.length > 0 && DateTime.fromJSDate(lastContact[0].created_date).diff(DateTime.fromJSDate(new Date()), 'days').toObject().days >= 10) {
        this.showEmailOnly = ([10, 15, 20, 25, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0);
      }

      this.showContact = ([4, 5, 6, 7, 8].indexOf(this.sensorFailure.alert_status) >= 0);

      if ([4].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactOption = 'Out of warranty email sent';
      }

      if ([6].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactOption = 'Quote accepted';
      }

      if ([5, 7, 8].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactOption = 'Sensor shipped';
      }

      if ([10, 20, 25, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactOption = 'Email note';
      }
      /*
            if ([6].indexOf(this.sensorFailure.alert_status) >= 0) {
              this.contactItems.push(
                {
                  label: 'Quote accepted', icon: 'pi pi-shopping-cart', command: () => {
                    this.quoteAccepted();
                  }
                });
            }
      */

      if ([4].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactItems.push(
          {
            label: 'Ship sensor', icon: 'pi pi-upload', command: () => {
              this.shipping();
            }
          });
      }

      if ([5].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactItems.push(
          {
            label: 'Quote sent', icon: 'pi pi-envelope', command: () => {
              this.sendQuote();
            }
          });
      }
      /*
            if ([4, 5, 6, 7, 8, 10, 15].indexOf(this.sensorFailure.alert_status) >= 0) {
              this.contactItems.push(
                {
                  label: 'Close alert', icon: 'pi pi-pause', command: () => {
                    this.noAction();
                  }
                });
            }
      */
      if ([4, 5, 6].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactItems.push(
          {
            label: 'Customer’s own stock', icon: 'pi pi-palette', command: () => {
              this.notNeeded();
            }
          });
      }

      if ([4, 5, 6].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.contactItems.push(
          {
            label: 'EI to replace', icon: 'pi pi-home', command: () => {
              this.notRequired();
            }
          });
      }

      if (lastContact !== undefined && lastContact.length > 0 && DateTime.fromJSDate(lastContact[0].created_date).diff(DateTime.fromJSDate(new Date()), 'days').toObject().days >= 10) {
        if ([4, 5, 6, 7, 8].indexOf(this.sensorFailure.alert_status) >= 0) {
          this.contactItems.push(
            {
              label: 'Email reminder', icon: 'pi pi-envelope', command: () => {
                this.emailNote();
              }
            });
        }
      }

      /* +========+ */
      /* | Action | */
      /* +========+ */
      this.showChangedOnly = ([8, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0);
      this.showReturnedOnly = ([45].indexOf(this.sensorFailure.alert_status) >= 0);
      this.showAction = ([10, 15].indexOf(this.sensorFailure.alert_status) >= 0);

      if ([8, 10, 15, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.actionOption = 'Sensor changed';
      }

      if ([45].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.actionOption = 'Sensor returned';
      }

      if ([10, 15].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.actionItems.push(
          {
            label: 'Sensor returned', icon: 'pi pi-reply', command: () => {
              this.sensorReturned();
            }
          });
      }
      if ([4, 5, 6, 7, 8, 30, 35].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.closeOption = 'Remove channel';
      } else {
        this.closeOption = 'Sensor Returned';
      }
      this.showClose = ([4, 5, 6, 7, 8, 30, 35, 45].indexOf(this.sensorFailure.alert_status) >= 0);
      if ([35, 45, 50, 60].indexOf(this.sensorFailure.alert_status) >= 0) {
        this.completionItems.push(
          {
            label: 'Close alert', command: () => {
              this.closeFailure();
            }
          });
        this.showReturnedOnly = false;
      }
    }
  }
  // Alert_Status	| Status_Description
  //       0	    | Reported
  //       4	    | Confirmed out of warranty
  //       5	    | Confirmed
  //       6	    | Awaiting customer PO
  //       7	    | Quote accepted
  //       8	    | Confirmed - customer use own stock
  //      10	    | Sensor Allocated
  //      15	    | Sensor Shipped
  //      20	    | On hold - Testing
  //      25	    | On hold - Investigating
  //      30	    | Old sensor returned - Need replacement confirmed
  //      35	    | Confirmed - EI to replace
  //      40	    | False Positive
  //      45	    | Sensor removed/Replaced
  //      50	    | Sensor removed/Replaced - Old sensor returned
  //      55	    | Acknowledged Cleared (False Positive)
  //      60	    | Sensor removed/Replaced - Old sensor NOT returned
  //      65	    | Sensor Failed - No action to be taken

  has(requiredPermissions: Permission | Permission[]) {
    return this.authService.hasPermission(requiredPermissions);
  }
}
